'use client';

import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { ModeToggle } from "@/components/mode-toggle";
import { LandingLanguageSwitcher } from "@/components/landing-language-switcher";
import { useLandingTranslations } from "@/hooks/useLandingTranslations";
import { normalizeLocale } from "@/i18n/config";

export function PublicLayout({ children }: { children: React.ReactNode }) {
  const { t, locale } = useLandingTranslations();
  const normalizedLocale = normalizeLocale(locale);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b">
        <div className="container mx-auto max-w-7xl p-4">
          <div className="flex items-center justify-between">
            <Link href={`/${normalizedLocale}`} className="flex items-center gap-3 group">
              <div className="relative w-10 h-10 flex-shrink-0 overflow-hidden">
                <Image
                  src="/logo.png"
                  alt="Vupt Market Logo"
                  width={40}
                  height={40}
                  className="w-full h-full object-contain transition-transform group-hover:scale-105"
                  priority
                />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-foreground via-foreground to-foreground/80 bg-clip-text text-transparent tracking-tight">
                Vupt Market
              </h1>
            </Link>
            <div className="flex items-center gap-4">
              <nav>
                <ul className="flex space-x-2">
                  <li>
                    <Button variant="ghost" asChild>
                      <Link href={`/${normalizedLocale}/affiliate-panel/login`}>
                        {t('landing.header.affiliate')}
                      </Link>
                    </Button>
                  </li>
                  <li>
                    <Button variant="ghost" asChild>
                      <Link href={`/${normalizedLocale}/developer-panel/login`}>
                        {t('landing.header.developer')}
                      </Link>
                    </Button>
                  </li>
                  <li>
                    <Button variant="ghost" asChild>
                      <Link href={`/${normalizedLocale}/login`}>{t('landing.header.merchant')}</Link>
                    </Button>
                  </li>
                </ul>
              </nav>
              <ModeToggle />
              <LandingLanguageSwitcher />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-grow">
        {children}
      </main>

      <footer className="border-t">
        <div className="container mx-auto max-w-7xl p-4 text-center text-sm text-muted-foreground space-y-2">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <Link
              href={`/${normalizedLocale}/terms-of-use`}
              className="underline underline-offset-4 hover:text-foreground transition-colors"
            >
              {t('landing.footer.termsOfUse')}
            </Link>
            <span className="text-muted-foreground/50" aria-hidden>
              ·
            </span>
            <Link
              href={`/${normalizedLocale}/privacy-policy`}
              className="underline underline-offset-4 hover:text-foreground transition-colors"
            >
              {t('landing.footer.privacyPolicy')}
            </Link>
          </div>
          <p>&copy; {new Date().getFullYear()} Vupt Market. {t('landing.footer.allRightsReserved')}</p>
          <p className="text-xs opacity-70">{t('landing.footer.poweredBy')}</p>
        </div>
      </footer>
    </div>
  );
}
